import currencyCodeOptions from '@/themes/shared/data/constants/currencyOptions';
import yesOrNoOptions from '@/themes/shared/data/constants/yesOrNoOptions';
import {
  ConditionalOperatorArray,
  ConditionalOperatorPrimitive,
  FormFieldSchemaType,
  SchemaAdditionalExpenseType,
  SchemaCancellationExpenseType,
  SchemaIncidentTiming,
  SchemaRadioYesOrNo,
  type FormFieldIsVisibleWhenCondition,
  type FormStepSchema,
  type SelectFieldOption,
} from '@/types';
import { generateUniqueIdentifierIds } from '@/utils/common';
import { schemaFieldName } from '../schemaFieldName';
import { schemaStepName } from '../schemaStepName';

interface GenerateNewClaimStepOtherExpensesFormSchemaParams {
  incidentTiming?: SchemaIncidentTiming;
  additionalExpenseIds?: string[];
  cancellationExpenseIds?: string[];
}

const { incident, expenses } = schemaFieldName;
const { cancellationExpenses, additionalExpenses } = expenses;

let stepOtherExpensesFormSchema: FormStepSchema = {
  name: schemaStepName.stepOtherExpenses,
  title: 'Tell us about your cancellation and other expenses',
  description: (
    <>
      Enter the amended or cancelled activities and/or bookings you want to claim. Depending on your
      circumstances, you may also be asked about any additional expenses. Claiming for multiple
      bookings or activities? You can add them all&nbsp;one&nbsp;by&nbsp;one.
      <br />
      <br />
      If you booked your travel arrangements through a travel agent, please download the{' '}
      <a target="_blank" rel="noopener noreferrer" href="/files/claim_agent-form.pdf">
        Agent&nbsp;form
      </a>{' '}
      and have your travel agent complete and submit it to the claims&nbsp;processing&nbsp;team.
    </>
  ),
  titleShort: 'Other expenses',
  lastUpdated: Date.now(),
  form: {
    name: `${schemaStepName.stepOtherExpenses}Form`,
    fields: [],
  },
};

// Maximum number of repeatable expenses in schema.
const MAX_NUMBER_OF_REPEATABLE_ADDITIONAL_EXPENSES = 10;

const MAX_NUMBER_OF_REPEATABLE_CANCELLATION_EXPENSES = 10;

let generatedAdditionalExpenseIds = generateUniqueIdentifierIds(
  MAX_NUMBER_OF_REPEATABLE_ADDITIONAL_EXPENSES,
);

let generatedCancellationExpenseIds = generateUniqueIdentifierIds(
  MAX_NUMBER_OF_REPEATABLE_CANCELLATION_EXPENSES,
);

let optionsForAdditionalExpenseIds = generatedAdditionalExpenseIds.map(expenseId => ({
  label: expenseId,
  value: expenseId,
}));

let optionsForCancellationExpenseIds = generatedCancellationExpenseIds.map(expenseId => ({
  label: expenseId,
  value: expenseId,
}));

// This combines and updates the relevant variables in 'global' scope.
function combineAndSetExistingAndGeneratedExpenseIdsForAdditionalExpenses(
  existingAdditionalExpenseIds: typeof generatedAdditionalExpenseIds,
) {
  generatedAdditionalExpenseIds = [
    ...existingAdditionalExpenseIds,
    ...generateUniqueIdentifierIds(MAX_NUMBER_OF_REPEATABLE_ADDITIONAL_EXPENSES),
  ].slice(0, MAX_NUMBER_OF_REPEATABLE_ADDITIONAL_EXPENSES);

  optionsForAdditionalExpenseIds = generatedAdditionalExpenseIds.map(expenseId => ({
    label: expenseId,
    value: expenseId,
  }));
}

// This combines and updates the relevant variables in 'global' scope.
function combineAndSetExistingAndGeneratedExpenseIdsForCancellationExpenses(
  existingCancellationExpenseIds: typeof generatedCancellationExpenseIds,
) {
  generatedCancellationExpenseIds = [
    ...existingCancellationExpenseIds,
    ...generateUniqueIdentifierIds(MAX_NUMBER_OF_REPEATABLE_CANCELLATION_EXPENSES),
  ].slice(0, MAX_NUMBER_OF_REPEATABLE_CANCELLATION_EXPENSES);

  optionsForCancellationExpenseIds = generatedCancellationExpenseIds.map(expenseId => ({
    label: expenseId,
    value: expenseId,
  }));
}

const additionalExpenseTypeOptions: SelectFieldOption<SchemaAdditionalExpenseType>[] = [
  {
    label: 'Accommodation',
    description: '(e.g. hotel or Airbnb)',
    value: SchemaAdditionalExpenseType.Accommodation,
  },
  {
    label: 'Flight',
    value: SchemaAdditionalExpenseType.Flight,
  },
  {
    label: 'Transport',
    description: '(e.g. ferries, trains, or buses)',
    value: SchemaAdditionalExpenseType.Transport,
  },
  {
    label: 'Other ',
    value: SchemaAdditionalExpenseType.Other,
  },
];

const cancellationExpenseTypeOptions: SelectFieldOption<SchemaCancellationExpenseType>[] = [
  {
    label: 'Accommodation',
    description: '(e.g. hotel or Airbnb)',
    value: SchemaCancellationExpenseType.Accommodation,
  },
  {
    label: 'Cruise',
    value: SchemaCancellationExpenseType.Cruise,
  },
  {
    label: 'Flight',
    value: SchemaCancellationExpenseType.Flight,
  },
  {
    label: 'Tour and activities ',
    value: SchemaCancellationExpenseType.Tour,
  },
  {
    label: 'Transport',
    description: '(e.g. ferries, trains, or buses)',
    value: SchemaCancellationExpenseType.Transport,
  },
  {
    label: 'Travel package',
    description: '(e.g. flights and accommodation booked together)',
    value: SchemaCancellationExpenseType.TravelPackage,
  },
  {
    label: 'Other ',
    value: SchemaCancellationExpenseType.Other,
  },
];

function getFieldNameForIndex(fieldName: string, index: number) {
  return fieldName.replace('{i}', `${index}`);
}

type FormStepSchemaFormFields = FormStepSchema['form']['fields'];
function generateCancellationExpensesFields(): FormStepSchemaFormFields {
  return generatedCancellationExpenseIds.flatMap((expenseId, index) => {
    const isExpenseVisibleWhen: FormFieldIsVisibleWhenCondition[] = [
      {
        field: expenses.hasCancellationExpense,
        value: SchemaRadioYesOrNo.Yes,
        operator: ConditionalOperatorPrimitive.Equal,
      },
      {
        value: [expenseId],
        field: expenses.cancellationExpenseIds,
        operator: ConditionalOperatorArray.Includes,
      },
    ];

    return [
      // Q5.2.1 - Type of cancellation expense.
      {
        type: FormFieldSchemaType.Select,
        name: getFieldNameForIndex(cancellationExpenses.expenseType, index),
        label:
          'Select the type of activity or booking you amended or cancelled because of the incident',
        description: 'Want to claim for multiple expenses? You can add more below.',
        layoutOptions: {
          hasSeparator: index !== 0,
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
        options: cancellationExpenseTypeOptions,
      },
      // Q5.2.1.1 - Tell us more (other type).
      {
        type: FormFieldSchemaType.Textarea,
        name: getFieldNameForIndex(cancellationExpenses.expenseDescription, index),
        label:
          "Please tell us more about the activity or booking you've had to cancel because of the incident",
        placeholder: 'Please tell us more.',
        isVisibleWhen: [
          ...isExpenseVisibleWhen,
          {
            field: getFieldNameForIndex(cancellationExpenses.expenseType, index),
            value: SchemaCancellationExpenseType.Other,
            operator: ConditionalOperatorPrimitive.Equal,
          },
        ],
      },
      // Q5.2.2 - Date of cancellation expense.
      {
        type: FormFieldSchemaType.Date,
        name: getFieldNameForIndex(cancellationExpenses.expenseDate, index),
        label: 'When was this booking amended or cancelled?',
        placeholder: 'dd/mm/yyyy',
        layoutOptions: {
          width: '1/2-block',
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
      },
      // Q5.2.3 - Cost of cancellation expense.
      {
        type: FormFieldSchemaType.Number,
        name: getFieldNameForIndex(cancellationExpenses.expenseAmount.amount, index),
        label: 'How much did this expense cost?',
        description: 'Use the amount on the invoice or bill',
        layoutOptions: {
          width: '1/2-inline',
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
      },
      {
        type: FormFieldSchemaType.Select,
        name: getFieldNameForIndex(cancellationExpenses.expenseAmount.currencyCode, index),
        label: 'How much did this expense cost?',
        description: 'Use the currency on the invoice or bill',
        layoutOptions: {
          width: '1/2-inline',
          isLabelInvisible: true,
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
        options: currencyCodeOptions,
      },
      // Q5.2.4 - Have refund or credit note?
      {
        type: FormFieldSchemaType.Radio,
        name: getFieldNameForIndex(cancellationExpenses.creditNoteOrRefundReceived, index),
        label: 'Did you receive a refund or credit note?',
        layoutOptions: {
          optionsPositioning: 'inline',
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
        options: yesOrNoOptions,
      },
      // Q5.2.4.1 - Credit note or refund amount.
      {
        type: FormFieldSchemaType.Number,
        name: getFieldNameForIndex(cancellationExpenses.creditNoteOrRefundAmount.amount, index),
        label: 'Enter refund or credit note amount',
        description: 'Use amount refunded or on the credit note',
        layoutOptions: {
          width: '1/2-inline',
        },
        isVisibleWhen: [
          ...isExpenseVisibleWhen,
          {
            field: getFieldNameForIndex(cancellationExpenses.creditNoteOrRefundReceived, index),
            value: SchemaRadioYesOrNo.Yes,
            operator: ConditionalOperatorPrimitive.Equal,
          },
        ],
      },
      {
        type: FormFieldSchemaType.Select,
        name: getFieldNameForIndex(
          cancellationExpenses.creditNoteOrRefundAmount.currencyCode,
          index,
        ),
        label: 'Enter refund or credit note amount',
        description: 'Use currency refunded or on the credit note',
        information: {
          description:
            'You need to submit evidence of the refund or credit note and original proof of payment when you submit your claim.',
          position: 'after',
        },
        layoutOptions: {
          width: '1/2-inline',
          isLabelInvisible: true,
        },
        isVisibleWhen: [
          ...isExpenseVisibleWhen,
          {
            field: getFieldNameForIndex(cancellationExpenses.creditNoteOrRefundReceived, index),
            value: SchemaRadioYesOrNo.Yes,
            operator: ConditionalOperatorPrimitive.Equal,
          },
        ],
        options: currencyCodeOptions,
      },
      // Q5.2.5 - Claim for cancellation expense.
      {
        type: FormFieldSchemaType.Number,
        name: getFieldNameForIndex(cancellationExpenses.claimAmount.amount, index),
        label: "Enter the amount you're claiming for",
        description: 'Enter amount',
        layoutOptions: {
          width: '1/2-inline',
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
      },
      {
        type: FormFieldSchemaType.Select,
        name: getFieldNameForIndex(cancellationExpenses.claimAmount.currencyCode, index),
        label: "Enter the amount you're claiming for",
        description: 'Select currency',
        layoutOptions: {
          width: '1/2-inline',
          isLabelInvisible: true,
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
        options: currencyCodeOptions,
      },
      // ID for this cancellation expense.
      {
        type: FormFieldSchemaType.Text,
        name: getFieldNameForIndex(cancellationExpenses.expenseId, index),
        label: 'Enter cancellation expense ID',
        value: expenseId,
        isRequired: false,
        layoutOptions: {
          isVisuallyHidden: true,
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
        repeatOptions: {
          action: 'remove',
          label: 'Remove this cancellation expense',
          field: expenses.cancellationExpenseIds,
          value: expenseId,
        },
      },
    ];
  });
}

function generateAdditionalExpensesFields(): FormStepSchemaFormFields {
  return generatedAdditionalExpenseIds.flatMap((expenseId, index) => {
    const isExpenseVisibleWhen: FormFieldIsVisibleWhenCondition[] = [
      {
        field: incident.incidentTiming,
        value: SchemaIncidentTiming.OnTrip,
        operator: ConditionalOperatorPrimitive.Equal,
      },
      {
        field: expenses.hasAdditionalExpense,
        value: SchemaRadioYesOrNo.Yes,
        operator: ConditionalOperatorPrimitive.Equal,
      },
      {
        value: [expenseId],
        field: expenses.additionalExpenseIds,
        operator: ConditionalOperatorArray.Includes,
      },
    ];

    return [
      // Q5.6.1 - Type of additional expense.
      {
        type: FormFieldSchemaType.Select,
        name: getFieldNameForIndex(additionalExpenses.expenseType, index),
        label: 'Select the type of additional expense paid for because of the incident',
        description: 'Want to claim for multiple expenses? You can add more below.',
        layoutOptions: {
          hasSeparator: index !== 0,
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
        options: additionalExpenseTypeOptions,
      },
      // Q5.6.1.1 - Tell us more (other type).
      {
        type: FormFieldSchemaType.Textarea,
        name: getFieldNameForIndex(additionalExpenses.expenseDescription, index),
        label:
          "Please tell us more about the additional expense you've had to pay for because of the incident",
        placeholder: 'Please tell us more.',
        isVisibleWhen: [
          ...isExpenseVisibleWhen,
          {
            field: getFieldNameForIndex(additionalExpenses.expenseType, index),
            value: SchemaAdditionalExpenseType.Other,
            operator: ConditionalOperatorPrimitive.Equal,
          },
        ],
      },
      // Q5.6.2 - Date of additional expense.
      {
        type: FormFieldSchemaType.Date,
        name: getFieldNameForIndex(additionalExpenses.expenseDate, index),
        label: 'When was this expense paid for?',
        placeholder: 'dd/mm/yyyy',
        layoutOptions: {
          width: '1/2-block',
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
      },
      // Q5.6.3 - Cost of additional expense.
      {
        type: FormFieldSchemaType.Number,
        name: getFieldNameForIndex(additionalExpenses.expenseAmount.amount, index),
        label: 'How much did this expense cost?',
        description: 'Use the amount on the invoice or bill',
        layoutOptions: {
          width: '1/2-inline',
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
      },
      {
        type: FormFieldSchemaType.Select,
        name: getFieldNameForIndex(additionalExpenses.expenseAmount.currencyCode, index),
        label: 'How much did this expense cost?',
        description: 'Use the currency on the invoice or bill',
        layoutOptions: {
          width: '1/2-inline',
          isLabelInvisible: true,
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
        options: currencyCodeOptions,
      },
      // Q5.6.4 - Have refund or credit note?
      {
        type: FormFieldSchemaType.Radio,
        name: getFieldNameForIndex(additionalExpenses.creditNoteOrRefundReceived, index),
        label: 'Did you receive a refund or credit note?',
        layoutOptions: {
          optionsPositioning: 'inline',
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
        options: yesOrNoOptions,
      },
      // Q5.6.4.1 - Credit note or refund amount.
      {
        type: FormFieldSchemaType.Number,
        name: getFieldNameForIndex(additionalExpenses.creditNoteOrRefundAmount.amount, index),
        label: 'Enter refund or credit note amount',
        description: 'Use amount refunded or on the credit note',
        layoutOptions: {
          width: '1/2-inline',
        },
        isVisibleWhen: [
          ...isExpenseVisibleWhen,
          {
            field: getFieldNameForIndex(additionalExpenses.creditNoteOrRefundReceived, index),
            value: SchemaRadioYesOrNo.Yes,
            operator: ConditionalOperatorPrimitive.Equal,
          },
        ],
      },
      {
        type: FormFieldSchemaType.Select,
        name: getFieldNameForIndex(additionalExpenses.creditNoteOrRefundAmount.currencyCode, index),
        label: 'Enter refund or credit note amount',
        description: 'Use currency refunded or on the credit note',
        information: {
          description:
            'You need to submit evidence of the refund or credit note and original proof of payment when you submit your claim.',
          position: 'after',
        },
        layoutOptions: {
          width: '1/2-inline',
          isLabelInvisible: true,
        },
        isVisibleWhen: [
          ...isExpenseVisibleWhen,
          {
            field: getFieldNameForIndex(additionalExpenses.creditNoteOrRefundReceived, index),
            value: SchemaRadioYesOrNo.Yes,
            operator: ConditionalOperatorPrimitive.Equal,
          },
        ],
        options: currencyCodeOptions,
      },
      // Q5.6.5 - Claim for additional expense.
      {
        type: FormFieldSchemaType.Number,
        name: getFieldNameForIndex(additionalExpenses.claimAmount.amount, index),
        label: "Enter the amount you're claiming for",
        description: 'Enter amount',
        layoutOptions: {
          width: '1/2-inline',
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
      },
      {
        type: FormFieldSchemaType.Select,
        name: getFieldNameForIndex(additionalExpenses.claimAmount.currencyCode, index),
        label: "Enter the amount you're claiming for",
        description: 'Select currency',
        layoutOptions: {
          width: '1/2-inline',
          isLabelInvisible: true,
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
        options: currencyCodeOptions,
      },
      // ID for this additional expense.
      {
        type: FormFieldSchemaType.Text,
        name: getFieldNameForIndex(additionalExpenses.expenseId, index),
        label: 'Enter additional expense ID',
        value: expenseId,
        isRequired: false,
        layoutOptions: {
          isVisuallyHidden: true,
        },
        isVisibleWhen: [...isExpenseVisibleWhen],
        repeatOptions: {
          action: 'remove',
          label: 'Remove this additional expense',
          field: expenses.additionalExpenseIds,
          value: expenseId,
        },
      },
    ];
  });
}

function generateNewClaimStepOtherExpensesFormSchema({
  incidentTiming = SchemaIncidentTiming.PreTrip,
  additionalExpenseIds = [],
  cancellationExpenseIds = [],
}: GenerateNewClaimStepOtherExpensesFormSchemaParams = {}): FormStepSchema {
  combineAndSetExistingAndGeneratedExpenseIdsForCancellationExpenses(cancellationExpenseIds);
  combineAndSetExistingAndGeneratedExpenseIdsForAdditionalExpenses(additionalExpenseIds);
  const { form, ...otherSchemaProps } = stepOtherExpensesFormSchema;
  const isPreTrip = incidentTiming === SchemaIncidentTiming.PreTrip;
  stepOtherExpensesFormSchema = {
    ...otherSchemaProps,
    lastUpdated: Date.now(),
    form: {
      name: form.name,
      fields: [
        // Q5.1 - Q5.2 - Have cancellation expense?
        {
          type: FormFieldSchemaType.Radio,
          name: expenses.hasCancellationExpense,
          title: 'Amendment and cancellation expenses',
          label: 'Do you have any amendment or cancellation expenses you want to claim?',
          value: isPreTrip ? SchemaRadioYesOrNo.Yes : undefined,
          isDisabled: isPreTrip,
          layoutOptions: {
            isVisuallyHidden: isPreTrip,
            optionsPositioning: 'inline',
          },
          options: yesOrNoOptions,
        },
        ...generateCancellationExpensesFields(),
        // IDs for tracking cancellation expenses.
        {
          type: FormFieldSchemaType.Checkbox,
          name: expenses.cancellationExpenseIds,
          label: 'Add or remove another cancellation expense',
          // If there are no expenses currently visible, then first expense is visible by default.
          value: cancellationExpenseIds.length
            ? cancellationExpenseIds
            : [generatedCancellationExpenseIds[0]],
          layoutOptions: {
            isVisuallyHidden: true,
          },
          isVisibleWhen: [
            {
              field: expenses.hasCancellationExpense,
              value: SchemaRadioYesOrNo.Yes,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: optionsForCancellationExpenseIds,
          repeatOptions: {
            action: 'add',
            label: 'Add another cancellation expense',
            values: optionsForCancellationExpenseIds.map(({ value }) => value),
          },
        },
        // Q5.5 - Q5.6 - Have additional expense?
        {
          type: FormFieldSchemaType.Radio,
          name: expenses.hasAdditionalExpense,
          title: 'Additional expenses',
          label: 'Do you have any additional expenses you want to claim?',
          layoutOptions: {
            hasSeparator: true,
            optionsPositioning: 'inline',
          },
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: yesOrNoOptions,
        },
        ...generateAdditionalExpensesFields(),
        // IDs for tracking additional expenses.
        {
          type: FormFieldSchemaType.Checkbox,
          name: expenses.additionalExpenseIds,
          label: 'Add or remove another additional expense',
          // If there are no expenses currently visible, then first expense is visible by default.
          value: additionalExpenseIds.length
            ? additionalExpenseIds
            : [generatedAdditionalExpenseIds[0]],
          layoutOptions: {
            isVisuallyHidden: true,
          },
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: expenses.hasAdditionalExpense,
              value: SchemaRadioYesOrNo.Yes,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: optionsForAdditionalExpenseIds,
          repeatOptions: {
            action: 'add',
            label: 'Add another additional expense',
            values: optionsForAdditionalExpenseIds.map(({ value }) => value),
          },
        },
        // Incident timing for this form step.
        {
          value: incidentTiming,
          name: incident.incidentTiming,
          type: FormFieldSchemaType.Select,
          label: 'Select trip status',
          isDisabled: true,
          layoutOptions: {
            isVisuallyHidden: true,
          },
          options: [
            {
              label: 'Pre-trip',
              value: SchemaIncidentTiming.PreTrip,
            },
            {
              label: 'On-trip',
              value: SchemaIncidentTiming.OnTrip,
            },
          ],
        },
      ],
    },
  };

  return stepOtherExpensesFormSchema;
}

function getNewClaimStepOtherExpensesFormSchema() {
  if (!stepOtherExpensesFormSchema.form.fields.length) {
    stepOtherExpensesFormSchema = generateNewClaimStepOtherExpensesFormSchema();
  }

  return stepOtherExpensesFormSchema;
}

export { generateNewClaimStepOtherExpensesFormSchema, getNewClaimStepOtherExpensesFormSchema };

export type { GenerateNewClaimStepOtherExpensesFormSchemaParams };
